import QRCode from 'qrcode'
import type { QrSettings } from '@cmms/types'

/// Headless QR-card renderer. Produces a single PNG canvas that
/// matches the visual composition in `components/QrCodeDialog.tsx`
/// (company → tagline → QR → title → subtitle → footer → logo), so
/// bulk exports from the Locations page look identical to the single
/// print/download from the sticker dialog.
///
/// Kept as a plain function (no React) so a caller can loop over a
/// selection of locations, get a canvas per item, and zip up the PNGs
/// without ever mounting a component. `qrcode`'s `toCanvas` writes to
/// a headless canvas we allocate here — no DOM insertion needed.

export interface QrCardInput {
  payload: string
  title: string
  subtitle?: string
  kind: string                       // 'Location' | 'Asset' | 'Site'
  qr?: Partial<QrSettings> | null    // org-level branding, from useOrgSettings
  logoBitmap?: HTMLImageElement | null // pre-loaded logo (optional)
}

export interface QrCardOutput {
  canvas: HTMLCanvasElement
  toBlob: () => Promise<Blob>
}

const DEFAULTS = {
  company_name:     '',
  tagline:          '',
  footer_text:      '',
  fg_color:         '#111827',
  bg_color:         '#FFFFFF',
  error_correction: 'M' as const,
}

export async function renderQrCard(input: QrCardInput): Promise<QrCardOutput> {
  const qr = { ...DEFAULTS, ...(input.qr ?? {}) }
  const companyName = qr.company_name.trim()
  const tagline     = qr.tagline.trim()
  const footerText  = qr.footer_text.trim()
    || `Scan with the mobile app to jump straight to this ${input.kind.toLowerCase()}.`
  const fgColor = qr.fg_color || DEFAULTS.fg_color
  const bgColor = qr.bg_color || DEFAULTS.bg_color
  const level: 'L' | 'M' | 'Q' | 'H' = qr.error_correction || 'M'

  // Layout constants mirror QrCodeDialog.composeCardCanvas() so
  // bulk-exported PNGs and hand-printed stickers look identical.
  const scale = 2
  const W = 360 * scale

  const padTop = 30 * scale
  const brandH = companyName ? 24 * scale : 0
  const taglineH = tagline ? 18 * scale : 0
  const brandGap = (companyName || tagline) ? 16 * scale : 0
  const qrSize = 280 * scale
  const qrGap = 24 * scale
  const titleH = 22 * scale
  const subtitleH = input.subtitle ? 18 * scale : 0
  const footerGap = footerText ? 16 * scale : 0
  const footerH = footerText ? 18 * scale : 0
  const logoGap = input.logoBitmap ? 14 * scale : 0
  const logoH = input.logoBitmap ? 44 * scale : 0
  const padBot = 28 * scale

  const H = padTop + brandH + taglineH + brandGap
          + qrSize + qrGap + titleH + subtitleH
          + footerGap + footerH + logoGap + logoH + padBot

  const canvas = document.createElement('canvas')
  canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D unavailable')

  ctx.fillStyle = bgColor
  ctx.fillRect(0, 0, W, H)

  let y = padTop

  if (companyName) {
    ctx.fillStyle = fgColor
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.font = `800 ${15 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
    ctx.fillText(truncate(companyName, 36), W / 2, y)
    y += brandH
  }
  if (tagline) {
    ctx.fillStyle = withAlpha(fgColor, 0.7)
    ctx.font = `500 ${11 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
    ctx.fillText(truncate(tagline, 56), W / 2, y)
    y += taglineH
  }
  if (companyName || tagline) y += brandGap - 4 * scale

  // Draw the QR into a temporary canvas at native resolution, then
  // blit it onto the main canvas without smoothing so modules stay
  // sharp — same trick QrCodeDialog uses.
  const qrCanvas = document.createElement('canvas')
  await QRCode.toCanvas(qrCanvas, input.payload, {
    errorCorrectionLevel: level,
    margin: 0,
    color: { dark: fgColor, light: bgColor },
    width: qrSize,
  })
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(qrCanvas, (W - qrSize) / 2, y, qrSize, qrSize)
  y += qrSize + qrGap

  ctx.fillStyle = fgColor
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.font = `800 ${18 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  ctx.fillText(truncate(input.title, 32), W / 2, y)
  y += titleH

  if (input.subtitle) {
    ctx.fillStyle = withAlpha(fgColor, 0.6)
    ctx.font = `500 ${13 * scale}px ui-monospace, SFMono-Regular, Menlo, monospace`
    ctx.fillText(truncate(input.subtitle, 40), W / 2, y)
    y += subtitleH
  }

  if (footerText) {
    y += footerGap
    ctx.fillStyle = withAlpha(fgColor, 0.55)
    ctx.font = `500 ${10.5 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
    ctx.fillText(truncate(footerText, 60), W / 2, y)
    y += footerH
  }

  if (input.logoBitmap && input.logoBitmap.complete && input.logoBitmap.naturalWidth > 0) {
    const img = input.logoBitmap
    const naturalRatio = img.naturalWidth / img.naturalHeight
    const maxW = 180 * scale
    let drawH = logoH
    let drawW = drawH * naturalRatio
    if (drawW > maxW) { drawW = maxW; drawH = drawW / naturalRatio }
    y += logoGap
    ctx.drawImage(img, (W - drawW) / 2, y, drawW, drawH)
  }

  return {
    canvas,
    toBlob: () => new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => {
        if (b) resolve(b)
        else reject(new Error('Failed to encode PNG'))
      }, 'image/png')
    }),
  }
}

/// Best-effort load of the org logo into an HTMLImageElement. Failures
/// are silently swallowed — bulk exports still succeed without the
/// logo. Kept out of `renderQrCard` so the caller can load once and
/// reuse across every card in a batch.
export function loadLogo(url: string | null): Promise<HTMLImageElement | null> {
  if (!url) return Promise.resolve(null)
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = url
  })
}

/// Filename-safe version of a display name — mirrors the sticker
/// dialog's `safeFilename` so single and bulk exports use the same
/// naming convention.
export function safeFilename(s: string): string {
  const cleaned = s.replace(/[^A-Za-z0-9 _-]/g, '').replace(/\s+/g, '-').toLowerCase()
  return cleaned || 'cmms'
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`
}

function withAlpha(hex: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return hex
  let r = 0, g = 0, b = 0
  const h = m[1]!
  if (h.length === 3) {
    r = parseInt(h[0]! + h[0]!, 16); g = parseInt(h[1]! + h[1]!, 16); b = parseInt(h[2]! + h[2]!, 16)
  } else {
    r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16)
  }
  return `rgba(${r}, ${g}, ${b}, ${a})`
}
